import React, { useCallback, useEffect, useState } from "react";
import { C } from "../../../components/cameras/CamerasStyles";
import { adminPinFromSession } from "../../../utils/securityApi";
import { useLightControls, useShopLights } from "../../../features/security/useShopLights";
import {
  autoOffNote,
  lightActivity,
  lightButtonsDisabled,
  lightHealthLine,
  lightStateView,
  pendingText,
} from "../../../features/security/lights";
import { phoneUi } from "../../ui/phoneUi";

// The phone Cameras screen's shop lights (2026-11-03): each area's lights on / off, where the after-hours auto-off
// stands, On / Off (Off asks on the phone's own sheet), and the auto-off switch. Layout only: rules and calls are
// features/security/lights.js + useShopLights.js. Mirrors PhoneLocks.jsx.

const TONE = { ok: C.live, bad: C.bad, warn: C.warn, muted: C.faint };
const NOTE = { success: C.live, danger: C.bad, warning: C.warn, info: C.textSoft };

function LightRow({ relay, data, readFailed, onChanged, onRefused }) {
  const { busy, msg, press, toggleAuto } = useLightControls({ relay, ui: phoneUi, onChanged, onRefused });
  const [open, setOpen] = useState(false);
  const view = lightStateView(relay, readFailed);
  const health = lightHealthLine(relay);
  const pending = pendingText(relay);
  const activity = lightActivity(data, relay.relay_id, 5);
  const off = busy || lightButtonsDisabled(relay);
  return (
    <div data-testid={`phone-light-${relay.relay_id}`} style={{ display: "grid", gap: 6, paddingTop: 6 }}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
              style={{ display: "flex", alignItems: "center", gap: 8, background: "transparent", border: 0, padding: 0, color: C.text, fontSize: 14, textAlign: "left" }}>
        <span style={{ flex: 1 }}>{relay.name}</span>
        <span style={{ color: TONE[view.tone], fontWeight: 700, fontSize: 13 }}>{view.label}</span>
      </button>
      {view.note && <span style={{ fontSize: 12, color: TONE[view.tone] }}>{view.note}</span>}
      {relay.state_text && <span style={{ fontSize: 12, color: C.textSoft }}>{relay.state_text}</span>}
      <div style={{ display: "flex", gap: 8 }}>
        <button type="button" className="jqc-btn" disabled={off} onClick={() => press("on")}>On</button>
        <button type="button" className="jqc-btn" disabled={off} onClick={() => press("off")}>Off</button>
      </div>
      {pending && <span style={{ fontSize: 12, color: C.muted }}>{pending}</span>}
      {msg && <span role="status" style={{ fontSize: 13, color: NOTE[msg.kind] || C.textSoft }}>{msg.text}</span>}
      {open && (
        <div style={{ display: "grid", gap: 4, fontSize: 12, color: C.muted }}>
          {health && <span>{health}</span>}
          <label style={{ display: "flex", alignItems: "center", gap: 8, color: C.textSoft, fontSize: 13 }}>
            <input type="checkbox" checked={Boolean(relay.auto_off)} disabled={busy} onChange={toggleAuto} />
            Auto-off after hours
          </label>
          <span>{autoOffNote(relay)}</span>
          {activity.map((a) => (
            <div key={a.key} style={{ display: "flex", justifyContent: "space-between", gap: 10, color: a.tone === "warn" ? C.warn : C.muted }}>
              <span>{a.text}</span><span>{a.when}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PhoneLights() {
  const [refused, setRefused] = useState(false);
  const onRefused = useCallback(() => setRefused(true), []);
  const pin = adminPinFromSession();
  const hasPin = Boolean(pin);
  useEffect(() => setRefused(false), [pin]);   // a new PIN gets a new try
  const { data, readFailed, load } = useShopLights({ active: hasPin && !refused, onRefused });
  const relays = (data && Array.isArray(data.relays) && data.relays) || [];
  if (!hasPin || refused) {
    return (
      <div data-testid="phone-lights-pin" style={{ padding: "6px 12px", borderBottom: `1px solid ${C.border}`, fontSize: 12, color: C.muted }}>
        Shop lights: tap the lock at the top and enter your Admin PIN to see them.
      </div>
    );
  }
  if (relays.length === 0) {
    if (!readFailed) return null;
    return (
      <div data-testid="phone-lights-failed" style={{ padding: "6px 12px", borderBottom: `1px solid ${C.border}`, fontSize: 12, color: C.bad }}>
        Shop lights: JobQ isn't answering, so they can't be shown right now.
      </div>
    );
  }
  return (
    <div data-testid="phone-lights" style={{ padding: "6px 12px 10px", borderBottom: `1px solid ${C.border}`, display: "grid", gap: 6 }}>
      <span style={{ fontSize: 12, color: C.faint, textTransform: "uppercase" }}>Shop lights</span>
      {relays.map((r) => (
        <LightRow key={r.relay_id} relay={r} data={data} readFailed={readFailed} onChanged={load} onRefused={onRefused} />
      ))}
    </div>
  );
}
